import { useState, useRef } from 'react';

export interface PhotoFile {
  id: string;
  url: string;
  file: File;
}

const MAX_PHOTOS = 3;

export const usePhotoHandler = () => {
  const [photos, setPhotos] = useState<PhotoFile[]>([]);
  const uploadInputRef = useRef<HTMLInputElement>(null);

  const handleUploadClick = () => {
    if (photos.length >= MAX_PHOTOS) {
      alert(`You can only upload a maximum of ${MAX_PHOTOS} photos.`);
      return;
    }
    
    if (uploadInputRef.current) {
      uploadInputRef.current.click();
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      const remainingSlots = MAX_PHOTOS - photos.length;
      
      if (remainingSlots <= 0) {
        alert(`You can only upload a maximum of ${MAX_PHOTOS} photos.`);
        return;
      }
      
      const filesToAdd = Array.from(files).slice(0, remainingSlots);
      const newPhotos = filesToAdd.map(file => ({
        id: Math.random().toString(36).substring(7),
        url: URL.createObjectURL(file),
        file
      }));
      
      setPhotos(prev => [...prev, ...newPhotos]);
      
      // Show notification if user tried to add more than allowed
      if (files.length > remainingSlots) {
        alert(`You can only upload ${remainingSlots} more photo${remainingSlots !== 1 ? 's' : ''}. Only the first ${remainingSlots} photo${remainingSlots !== 1 ? 's' : ''} were added.`);
      }
    }
    // Reset input value so the same file can be selected again
    if (uploadInputRef.current) {
      uploadInputRef.current.value = '';
    }
  };

  const handleRemovePhoto = (id: string) => {
    setPhotos(prev => {
      const photoToRemove = prev.find(photo => photo.id === id);
      if (photoToRemove) {
        URL.revokeObjectURL(photoToRemove.url);
      }
      return prev.filter(photo => photo.id !== id);
    });
  };

  const canAddMorePhotos = photos.length < MAX_PHOTOS;
  const remainingPhotoSlots = MAX_PHOTOS - photos.length;

  return {
    photos,
    setPhotos,
    uploadInputRef,
    handleUploadClick,
    handleFileSelect,
    handleRemovePhoto,
    canAddMorePhotos,
    remainingPhotoSlots,
    maxPhotos: MAX_PHOTOS,
  };
}; 